import i18n from '@/plugins/i18n'
import moment from 'moment';

const calculateDifference = (actual, ideal) => {
    if (!isNaN(actual) && !isNaN(ideal) && ideal !== 0) {
        let difference = ((actual * 100) / ideal) - 100
        if (difference < 0) difference = difference * -1
        return difference % 1 === 0 ? difference.toFixed(0) : difference.toFixed(2)
    }
    return 0
}

export const getValuesForEvaluation = (assignedEvaluationValues = null) => {
    const values = {}

    assignedEvaluationValues?.forEach(item => {
        for (const key in item) {
            if (key !== "date") {
                const value = parseFloat(item[key]?.toString().replace(',', '.'))
                if (isNaN(value)) continue;
                if (!values[key]) values[key] = { best: -Infinity, worst: Infinity, sum: 0, count: 0 }
                values[key].sum += value
                values[key].count++
                values[key].best = Math.max(values[key].best, value)
                values[key].worst = Math.min(values[key].worst, value)
                values[key].actual = value
                values[key].all = [...values[key].all || [], value]
            }
        }
    })

    for (const key in values) {
        const average = values[key].average = values[key].sum / values[key].count
        values[key].average = average % 1 === 0 ? average.toFixed(0) : average.toFixed(2)
    }
    return values
}

export const getItemsForEvaluation = (currentConfig, currentValues) => {
    let items = []
    currentConfig.forEach((config, i) => {

        let currentValue = currentValues[config.id]
        if (currentValue) {
            let actual = parseFloat(currentValue.actual)
            let ideal = parseFloat(config.value)
            let color = config.upgrade ? (actual > ideal ? 'success' : 'error') : (actual > ideal ? 'error' : 'success')

            let payload = {
                id: config.id,
                name: config.name,
                unit: i18n.t(`units.${config.unit}_short`),
                actual: actual,
                difference: `${calculateDifference(actual, ideal)}%`,
                color: color,
                best: config.upgrade ? currentValue.best : currentValue.worst,
                worst: config.upgrade ? currentValue.worst : currentValue.best,
                average: currentValue.average,
                ideal: ideal,
                upgrade: config.upgrade,
            }

            items.push(payload)
        }
    })

    return items
}

export const getCurrentValuesForEvaluation = (currentValues, currentConfig, currentItems) => {
    let values = currentValues[currentConfig.id]
    if (!values) return null
    return {
        ...values,
        difference: calculateDifference(values.actual, currentConfig.value),
        color: currentItems?.find(item => item.id === currentConfig.id)?.color === 'error' ? 'red' : 'black',
        best: currentConfig.upgrade ? values.best : values.worst,
        worst: currentConfig.upgrade ? values.worst : values.best,
    }
}

export const getCurrentValuesArrayForEvaluation = (currentValues, currentConfig, currentItems) => {
    return Object.entries(currentValues).map(([id, values]) => {
        const config = currentConfig?.find(cfg => cfg.id === id)
        if (!config) return null
        let evaluation = currentItems?.find(item => item.id === id) || null

        return {
            ...values,
            id,
            difference: calculateDifference(values.actual, config.value),
            color: evaluation?.color === 'error' ? 'red' : 'black',
            name: evaluation?.name || config.name,
            best: config.upgrade ? values.best : values.worst,
            worst: config.upgrade ? values.worst : values.best,
        }
    }).filter(v => v !== null)
}

export const getCurrentValuesDatesForEvaluation = (assignedEvaluationValues = null, currentConfigDates) => {
    return assignedEvaluationValues?.map(val => {
        return {
            actual: parseFloat(val[currentConfigDates.id]?.toString().replace(',', '.')),
            date: moment(val.date, 'DD-MM-YYYY').format('DD MMM').replace('.', '')
        }
    })
}

export const getDatasetsForEvaluation = (items) => {
    const scaledIdeal = [];
    const scaledActual = [];
    const idealReal = [];
    const actualReal = [];

    items.forEach(item => {
        const realIdeal = item.ideal;
        const realActual = item.actual;
        const maxVal = Math.max(realIdeal, realActual);
        if (maxVal === 0) {
            scaledIdeal.push(0);
            scaledActual.push(0);
        } else {
            scaledIdeal.push((realIdeal / maxVal) * 3);
            scaledActual.push((realActual / maxVal) * 3);
        }

        idealReal.push(realIdeal);
        actualReal.push(realActual);
    });

    return [
        {
            label: i18n.t('common.ideal'),
            backgroundColor: 'rgba(51,153,153,0.07)',
            borderColor: 'rgba(51,153,153,1)',
            borderDash: [6, 4],
            pointBackgroundColor: 'rgba(51,153,153,1)',
            pointBorderColor: 'rgba(51,153,153,1)',
            pointHoverBackgroundColor: 'rgba(51,153,153,1)',
            pointHoverBorderColor: 'rgba(51,153,153,1)',
            data: scaledIdeal,
            dataReal: idealReal,
        },
        {
            label: i18n.t('common.actual'),
            backgroundColor: 'rgba(204,255,51,0.18)',
            borderColor: 'rgba(204,255,51,1)',
            pointBackgroundColor: 'rgba(204,255,51,1)',
            pointBorderColor: 'rgba(204,255,51,1)',
            pointHoverBackgroundColor: 'rgba(204,255,51,1)',
            pointHoverBorderColor: 'rgba(204,255,51,1)',
            data: scaledActual,
            dataReal: actualReal,
        }
    ];
}

export const getDatasetsCompareForEvaluation = (studentItems, groupItems) => {
    const scaledStudent = []
    const scaledGroup = []
    const realStudent = []
    const realGroup = []

    studentItems.forEach(item => {
        const groupItem = groupItems.find(group => group.id === item.id) || null
        const studentValue = Number(item.actual) || 0
        const groupValue = Number(groupItem?.actual) || 0
        const maxVal = Math.max(studentValue, groupValue)

        scaledStudent.push(maxVal === 0 ? 0 : (studentValue / maxVal) * 3)
        scaledGroup.push(maxVal === 0 ? 0 : (groupValue / maxVal) * 3)
        realStudent.push(studentValue)
        realGroup.push(groupValue)
    })

    return [
        {
            label: i18n.t('common.student'),
            backgroundColor: 'rgba(204,255,51,0.18)',
            borderColor: 'rgba(204,255,51,1)',
            pointBackgroundColor: 'rgba(204,255,51,1)',
            pointBorderColor: 'rgba(204,255,51,1)',
            pointHoverBackgroundColor: 'rgba(204,255,51,1)',
            pointHoverBorderColor: 'rgba(204,255,51,1)',
            data: scaledStudent,
            dataReal: realStudent,
        },
        {
            label: i18n.t('common.group'),
            backgroundColor: 'rgba(51,153,153,0.07)',
            borderColor: 'rgba(51,153,153,1)',
            borderDash: [6, 4],
            pointBackgroundColor: 'rgba(51,153,153,1)',
            pointBorderColor: 'rgba(51,153,153,1)',
            pointHoverBackgroundColor: 'rgba(51,153,153,1)',
            pointHoverBorderColor: 'rgba(51,153,153,1)',
            data: scaledGroup,
            dataReal: realGroup,
        }
    ]
}

export const getDatasetsTeamForEvaluation = (studentItems, groupItems, teamItems) => {
    const allItems = [studentItems, groupItems, teamItems].flat();

    const globalMax = Math.max(...allItems.map(item => item.actual || 1));

    const scaleData = (items, useAvg = false) => {
        const scaled = [];
        const real = [];
        items.forEach(item => {
            const realValue = useAvg ? (item.average ?? item.actual) : item.actual;
            scaled.push((realValue / globalMax) * 3);
            real.push(realValue);
        });
        return { scaled, real };
    };

    const student = scaleData(studentItems, false);
    const group = scaleData(groupItems, true);
    const team = scaleData(teamItems, true);

    return [
        {
            label: i18n.t('common.actual'),
            backgroundColor: 'rgba(204,255,153,0.2)',
            borderColor: 'rgba(204,255,153,1)',
            pointBackgroundColor: 'rgba(204,255,153,1)',
            pointBorderColor: 'rgba(204,255,153,1)',
            pointHoverBackgroundColor: 'rgba(204,255,153,1)',
            pointHoverBorderColor: 'rgba(204,255,153,1)',
            data: student.scaled,
            dataReal: student.real,
        },
        {
            label: i18n.t('common.group'),
            backgroundColor: 'rgba(7, 133, 113, 0.2)',
            borderColor: 'rgba(7, 133, 113, 1)',
            pointBackgroundColor: 'rgba(7, 133, 113, 1)',
            pointBorderColor: 'rgba(7, 133, 113, 1)',
            pointHoverBackgroundColor: 'rgba(7, 133, 113, 1)',
            pointHoverBorderColor: 'rgba(7, 133, 113, 1)',
            data: group.scaled,
            dataReal: group.real,
        },
        {
            label: i18n.t('common.team'),
            backgroundColor: 'rgba(139,124,246,0.2)',
            borderColor: 'rgba(139,124,246,1)',
            pointBackgroundColor: 'rgba(139,124,246,1)',
            pointBorderColor: 'rgba(139,124,246,1)',
            pointHoverBackgroundColor: 'rgba(139,124,246,1)',
            pointHoverBorderColor: 'rgba(139,124,246,1)',
            data: team.scaled,
            dataReal: team.real,
        }
    ];
}

export const getValuesGroupedForEvaluation = (currentValues, currentStudentId = null, currentGroup = null) => {
    if (currentStudentId) {
        return currentValues.map(value => {
            return {
                date: value.date,
                ...value[currentStudentId]
            }
        }) || []
    }
    if (currentGroup) {
        let studentsIds = currentGroup?.students || []
        return currentValues.map(entry => {
            const sums = {}
            const counts = {}
            const date = entry.date

            Object.keys(entry).forEach(studentId => {
                if (studentId === 'date' || !studentsIds.includes(studentId)) return
                const evaluations = entry[studentId]

                Object.entries(evaluations).forEach(([evalId, value]) => {
                    const num = parseFloat(value?.toString().replace(',', '.'))
                    if (!isNaN(num)) {
                        sums[evalId] = (sums[evalId] || 0) + num
                        counts[evalId] = (counts[evalId] || 0) + 1
                    }
                })
            })

            const result = { date }
            Object.keys(sums).forEach(evalId => {
                result[evalId] = (sums[evalId] / counts[evalId]).toFixed(2)
            })

            return result
        })
    }
    return currentValues.map(entry => {
        const sums = {}
        const counts = {}
        const date = entry.date

        Object.keys(entry).forEach(studentId => {
            if (studentId === 'date') return
            const evaluations = entry[studentId]

            Object.entries(evaluations).forEach(([evalId, value]) => {
                const num = parseFloat(value?.toString().replace(',', '.'))
                if (!isNaN(num)) {
                    sums[evalId] = (sums[evalId] || 0) + num
                    counts[evalId] = (counts[evalId] || 0) + 1
                }
            })
        })

        const result = { date }
        Object.keys(sums).forEach(evalId => {
            result[evalId] = (sums[evalId] / counts[evalId]).toFixed(2)
        })

        return result
    })
}